import { Box, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type {
  AtterbergContext,
  AtterbergGeneral,
  AtterbergDetail,
} from '../../../types/atterberg';
import { applyOverrides, computeAtterbergResult, computeTrial } from './computations';
import { useTestFormulas } from '../../../hooks/useTestFormulas';
import { FormulaHeaderTooltip } from '../components/FormulaHeaderTooltip';

interface Props {
  context: AtterbergContext;
  general: AtterbergGeneral;
  llTrials: AtterbergDetail[];
  plTrials: AtterbergDetail[];
  retestLLTrials?: AtterbergDetail[];
}

const fmt = (v: number | null | undefined, d = 2): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toFixed(d);
};

const fmtInt = (v: number | null): string => v === null ? '—' : String(v);

export const AtterbergSummaryTable = ({ context, general, llTrials, plTrials, retestLLTrials }: Props) => {
  const theme = useTheme();
  // Computed-value formula strings come from the golden-linked Formula Registry.
  const { formulaFor } = useTestFormulas('atterberg');
  const method = general.llMethod ?? 'Multipoint';
  const computed = computeAtterbergResult({
    llTrials, plTrials, retestLLTrials, method,
    isNonPlastic: general.isNonPlastic ?? false,
  });
  const final = applyOverrides(general, computed);

  const llComputed = llTrials.map((t) => computeTrial(t, { method }));
  const plComputed = plTrials.map((t) => computeTrial(t, { method }));
  const plAvg = plComputed
    .map((c) => c.waterContent)
    .filter((v): v is number => v !== null);
  const plAvgValue = plAvg.length ? plAvg.reduce((a, b) => a + b, 0) / plAvg.length : null;

  const headerSx = {
    fontSize: '0.78rem', fontWeight: 700, color: 'primary.main', letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
  };

  const tableHeader = {
    fontSize: '0.72rem', fontWeight: 700, color: 'text.primary',
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    py: 0.4, px: 0.75, textAlign: 'center' as const, borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  };
  const tableLabel = {
    fontSize: '0.72rem', py: 0.4, px: 0.75, color: 'text.secondary',
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  };
  const tableCell = {
    fontSize: '0.74rem', py: 0.4, px: 0.75, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const,
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  };

  const cardSx = {
    p: 1.5, borderRadius: 2,
    border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
      {/* Project + Sample info */}
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Project Information</Typography>
        <Box sx={{ mt: 0.75 }}>
          <InfoRow label="Project No." value={context.projectId || '—'} />
          <InfoRow label="Project Name" value={context.projectName || '—'} />
          <InfoRow label="Project Location" value={context.siteName || '—'} />
          <InfoRow label="Client" value={context.clientName || '—'} />
        </Box>
      </Paper>

      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Sample Information</Typography>
        <Box sx={{ mt: 0.75 }}>
          <InfoRow label="Borehole / Test Pit" value={context.locationName || '—'} />
          <InfoRow
            label="Sample ID"
            value={context.specimenRef
              ? `${context.sampleReference} (${context.specimenRef})`
              : (context.sampleReference || '—')}
          />
          <InfoRow
            label="Sample Depth"
            value={context.specimenDepth != null
              ? `${context.specimenDepth} m`
              : (context.depth ? `${context.depth} m` : '—')}
          />
          <InfoRow label="Sample Type" value={context.sampleType || '—'} />
        </Box>
      </Paper>

      {/* Liquid Limit table */}
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Liquid Limit</Typography>
        <Box sx={{
          mt: 0.75,
          display: 'grid',
          gridTemplateColumns: '1.4fr repeat(4, 1fr)',
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 1,
        }}>
          <Box sx={tableHeader}>Trial</Box>
          {[1, 2, 3, 4].map((n) => (
            <Box key={`llh-${n}`} sx={tableHeader}>{n}</Box>
          ))}
          <FormulaHeaderTooltip label="Mass of Moist Soil (g)" formula={formulaFor('massOfMoistSoil')?.formula}>
            <Box sx={tableLabel}>Mass of Moist Soil (g)</Box>
          </FormulaHeaderTooltip>
          {llComputed.map((c, i) => <Box key={`llm-${i}`} sx={tableCell}>{fmt(c.massOfMoistSoil, 2)}</Box>)}
          <FormulaHeaderTooltip label="Mass of Dry Soil (g)" formula={formulaFor('massOfDrySoil')?.formula}>
            <Box sx={tableLabel}>Mass of Dry Soil (g)</Box>
          </FormulaHeaderTooltip>
          {llComputed.map((c, i) => <Box key={`lld-${i}`} sx={tableCell}>{fmt(c.massOfDrySoil, 2)}</Box>)}
          <FormulaHeaderTooltip label="Water Content (%)" formula={formulaFor('waterContent')?.formula}>
            <Box sx={tableLabel}>Water Content (%)</Box>
          </FormulaHeaderTooltip>
          {llComputed.map((c, i) => <Box key={`llw-${i}`} sx={tableCell}>{fmt(c.waterContent, 1)}</Box>)}
          <Box sx={tableLabel}>No. of Blows (N)</Box>
          {llTrials.map((t, i) => <Box key={`lln-${i}`} sx={tableCell}>{fmtInt(t.numberOfBlows)}</Box>)}
        </Box>
      </Paper>

      {/* Plastic Limit table */}
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Plastic Limit</Typography>
        <Box sx={{
          mt: 0.75,
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 1fr',
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 1,
        }}>
          <Box sx={tableHeader}>Trial</Box>
          {[1, 2].map((n) => (
            <Box key={`plh-${n}`} sx={tableHeader}>{n}</Box>
          ))}
          <FormulaHeaderTooltip label="Mass of Moist Soil (g)" formula={formulaFor('massOfMoistSoil')?.formula}>
            <Box sx={tableLabel}>Mass of Moist Soil (g)</Box>
          </FormulaHeaderTooltip>
          {plComputed.map((c, i) => <Box key={`plm-${i}`} sx={tableCell}>{fmt(c.massOfMoistSoil, 2)}</Box>)}
          <FormulaHeaderTooltip label="Mass of Dry Soil (g)" formula={formulaFor('massOfDrySoil')?.formula}>
            <Box sx={tableLabel}>Mass of Dry Soil (g)</Box>
          </FormulaHeaderTooltip>
          {plComputed.map((c, i) => <Box key={`pld-${i}`} sx={tableCell}>{fmt(c.massOfDrySoil, 2)}</Box>)}
          <FormulaHeaderTooltip label="Water Content (%)" formula={formulaFor('waterContent')?.formula}>
            <Box sx={tableLabel}>Water Content (%)</Box>
          </FormulaHeaderTooltip>
          {plComputed.map((c, i) => <Box key={`plw-${i}`} sx={tableCell}>{fmt(c.waterContent, 1)}</Box>)}
          <Box sx={{ ...tableLabel, gridColumn: '1 / 2' }}>Avg. Water Content (%)</Box>
          <Box sx={{ ...tableCell, gridColumn: '2 / 4', textAlign: 'center' }}>{fmt(plAvgValue, 1)}</Box>
        </Box>
      </Paper>

      {/* Results + USCS */}
      <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography sx={headerSx}>Results</Typography>
            <Box sx={{
              mt: 0.75,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 1.5,
            }}>
              <ResultStat label="Liquid Limit" value={general.isNonPlastic ? 'NP' : fmtInt(final.ll)} formula={formulaFor('ll')?.formula} />
              <ResultStat label="Plastic Limit" value={general.isNonPlastic ? 'NP' : fmtInt(final.pl)} formula={formulaFor('pl')?.formula} />
              <ResultStat label="Plasticity Index" value={general.isNonPlastic ? 'NP' : fmtInt(final.pi)} formula={formulaFor('pi')?.formula} />
            </Box>
          </Box>
          <Box>
            <Typography sx={headerSx}>USCS Group Symbol (D2487)</Typography>
            <Typography sx={{
              fontSize: '2.4rem', fontWeight: 800, textAlign: 'center', mt: 0.5,
              color: final.uscs ? 'primary.main' : 'text.disabled',
            }}>
              {final.uscs ?? '—'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'baseline',
    py: 0.3, borderBottom: '1px solid', borderColor: 'divider',
  }}>
    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Box>
);

const ResultStat = ({ label, value, formula }: { label: string; value: string; formula?: string }) => {
  const labelEl = (
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
  );
  return (
    <Box>
      {formula
        ? <FormulaHeaderTooltip label={label} formula={formula}>{labelEl}</FormulaHeaderTooltip>
        : labelEl}
      <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: value === '—' ? 'text.disabled' : 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
};
